import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { SummaryTile } from "./components/SummaryTile";
import { MeetingTypeBarCharts } from "./components/MeetingTypeBarCharts";
import { PieChartSection } from "./components/PieChartSection";
import { useDashboardData, useBusinesses } from "@/services/dashboard";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { trackPageView } from "@/lib/analytics";

const TILES = [
  {
    key: "totalDemandForDU" as const,
    title: "Total Demand for DU",
    tooltipText: "Total number of room nights requested for the fiscal year."
  },
  {
    key: "totalRoomNightsScheduled" as const,
    title: "Total Room Nights Scheduled",
    tooltipText: "Sum of all room nights scheduled or resolved at DU for the fiscal year."
  },
  {
    key: "totalMeetingsRequested" as const,
    title: "Total # of Meetings Requested",
    tooltipText: "Count of all meetings submitted for scheduling at DU."
  },
  {
    key: "totalMeetingsScheduled" as const,
    title: "Total # of Meetings Scheduled",
    tooltipText: "Count of meetings confirmed or resolved at DU."
  },
  {
    key: "usedPracticalCapcity" as const,
    title: "% Used of Practical Capacity",
    tooltipText: "Percentage of DU’s practical guest room capacity based on 327,600 total room nights available in FY 27."
  }
];

export function DashboardPage() {
  const currentUser = useCurrentUser();
  const userId = currentUser?.email ?? "Unknown";

  // Track page view on mount
  useEffect(() => {
    trackPageView("Dashboard", userId);
  }, [userId]);

  const [topBusiness, setTopBusiness] = useState<string | null>(null);
  const [pieRoomBusiness, setPieRoomBusiness] = useState<string | null>(null);
  const [pieDuBusiness, setPieDuBusiness] = useState<string | null>(null);

  const hasTopFilter = topBusiness !== null;

  const mainQuery = useDashboardData(hasTopFilter, topBusiness);

  // Per-chart drilldown queries — only active when no top filter
  const pieRoomQuery = useDashboardData(false, pieRoomBusiness);
  const pieDuQuery = useDashboardData(false, pieDuBusiness);

  const { data: businessesMap } = useBusinesses();
  const allBusinesses = businessesMap ? Object.keys(businessesMap) : [];

  const data = mainQuery.data;
  const showInitialLoadingOverlay = mainQuery.isPending && !data;
  const showRefreshLoadingMessage = mainQuery.isFetching && !!data;

  // Room nights pie: top filter → businessLine from main query; per-chart → drilldown query
  const roomNightsData = data?.totalRoomNightsByBusiness ?? [];
  const roomNightsLineData = hasTopFilter
    ? (data?.totalRoomNightsByBusinessLine ?? [])
    : (pieRoomQuery.data?.totalRoomNightsByBusinessLine ?? []);

  // DU percentage pie: same pattern
  const duPercentData = data?.duPercentageRoomNightsByBusiness ?? [];
  const duPercentLineData = hasTopFilter
    ? (data?.duPercentageRoomNightsByBusinessLine ?? [])
    : (pieDuQuery.data?.duPercentageRoomNightsByBusinessLine ?? []);

  const handleTopBusinessChange = (value: string | null) => {
    setTopBusiness(value);
    // Sync pie chart selections when top filter changes
    setPieRoomBusiness(value);
    setPieDuBusiness(value);
  };

  return (
    <div className="dashboard">
      {showInitialLoadingOverlay && (
        <div className="loading-overlay-react" role="status" aria-live="polite">
          <Loader2
            className="h-10 w-10 animate-spin text-brand"
            aria-label="Loading dashboard"
          />
        </div>
      )}

      {showRefreshLoadingMessage && (
        <div className="dashboard-refresh-overlay" role="status" aria-live="polite">
          <div className="dashboard-refresh-message">
            <Loader2 className="h-5 w-5 animate-spin text-brand" aria-hidden="true" />
            <span>Loading updated dashboard data...</span>
          </div>
        </div>
      )}

      <div className="page-header">
        <h1>Draft Calendar Metrics Dashboard</h1>

        <div className="filters">
            <label htmlFor="top-business-filter" className="sr-only">
              Filter by business
            </label>
            <select
              id="top-business-filter"
              value={topBusiness ?? ""}
              onChange={(e) => handleTopBusinessChange(e.target.value || null)}
              className="dropdown"
            >
              <option value="">All of DU</option>
              {allBusinesses.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>

            <select
              aria-label="Fiscal year"
              defaultValue="FY 2027"
              className="dropdown fiscal-year"
            >
              <option>FY 2027</option>
            </select>
        </div>
      </div>

      <div className="main-content">
        {mainQuery.isError && (
          <p role="alert" className="dashboard-error-alert">
            Failed to load dashboard data. Please try refreshing the page.
          </p>
        )}

        {data && (
          <>
            <section aria-label="Summary metrics" className="stats-row">
              {TILES.map((tile) => (
                <SummaryTile
                  key={tile.key}
                  title={tile.title}
                  value={data[tile.key] ?? "—"}
                  tooltipText={tile.tooltipText}
                />
              ))}
            </section>

            <section aria-label="Programs by meeting type" className="charts-row">
              <MeetingTypeBarCharts data={data.totalProgramByMeetingType ?? []} />
            </section>

            <section aria-label="Room nights by business" className="charts-row">
              <PieChartSection
                title="Total # of Room Nights by Business"
                tooltipText="Total number of room nights booked by each business or business line."
                data={roomNightsData}
                businessLineData={roomNightsLineData}
                allBusinesses={allBusinesses}
                otherHoverItems={data.otherBusinessTotalRoomNightHover}
                disabled={hasTopFilter}
                selectedBusiness={hasTopFilter ? topBusiness : pieRoomBusiness}
                onBusinessChange={setPieRoomBusiness}
              />
              <PieChartSection
                title="% of DU Room Nights by Business / Line"
                tooltipText="Percentage share of total DU room nights by business or business line."
                data={duPercentData}
                businessLineData={duPercentLineData}
                allBusinesses={allBusinesses}
                otherHoverItems={data.otherBusinessDUPercentageRoomNightsHover}
                disabled={hasTopFilter}
                selectedBusiness={hasTopFilter ? topBusiness : pieDuBusiness}
                onBusinessChange={setPieDuBusiness}
              />
            </section>

            {data.lastUpdated && (
              <div className="last-updated">
                <p>
                  <strong>Last updated:</strong> {data.lastUpdated}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
